import { Suspense, useState } from 'react';

import { getAccountsQueryConfig } from '@api/lendingAPI/queryConfigs';
import { TFormalOffer } from '@helpers/FormalOffers/formalOffers.types';
import { getFormalOffersLatestOfferConfiguration } from '@helpers/FormalOffers/getFormalOffersLatestOfferConfiguration';
import { getTimeOutDate } from '@helpers/FormalOffers/getTimeOutDate';
import {
    fetchGetFormalOffers,
    fetchGetFormalOffersLatestOfferConfiguration
} from '@iwoca/lapi-client/edge';
import { useQuery } from '@tanstack/react-query';

import { getOfferSetType } from './getOfferSetType';
import { MoreFundsOnly } from './MoreFundsOnly/MoreFundsOnly';
import { MoreFundsWithRepaymentChoice } from './MoreFundsWithRepaymentChoice/MoreFundsWithRepaymentChoice';
import { LoadingSkeleton } from './PageContainer';
import { RepaymentReductionOnly } from './RepaymentReductionOnly/RepaymentReductionOnly';
import { RepaymentReductionWithChoice } from './RepaymentReductionWithChoice/RepaymentReductionWithChoice';
import { ErrorBoundary } from '../../../../../errorBoundary/ErrorBoundary';
import { useStateKey } from '../../../../../iwQueryClient/useStateKey';
import FallbackErrorModal from '../../../../modals/fallbackErrorModal/FallbackErrorModal';
import { useWriteOfferStep } from '../../../../myOffers/offerFlowMemory/useWriteOfferStep';

export const Approved = ({ accountId }: { accountId: string }) => {
    return (
        <ErrorBoundary
            fallback={(error) => <FallbackErrorModal error={error} />}
        >
            <Suspense
                fallback={
                    <LoadingSkeleton data-testid="approved-loading-skeleton" />
                }
            >
                <Offers accountId={accountId} />
            </Suspense>
        </ErrorBoundary>
    );
};

const Offers = ({ accountId }: { accountId: string }) => {
    const stateKey = useStateKey();

    const { data: formalOffersResponse } = useQuery({
        queryKey: ['fetchGetFormalOffers', stateKey],
        queryFn: () => fetchGetFormalOffers({ stateKey }),
        select: (data) => data.data.formal_offers,
        suspense: true
    });
    const { data: accountsResponse } = useQuery({
        ...getAccountsQueryConfig(stateKey),
        select: (data) => data.data!,
        suspense: true
    });
    const { data: latestOfferConfiguration } = useQuery({
        queryKey: ['fetchGetFormalOffersLatestOfferConfiguration', stateKey],
        queryFn: () =>
            fetchGetFormalOffersLatestOfferConfiguration({ stateKey }),
        select: (data) => data.data.latest_offer_configuration,
        suspense: true
    });

    const formalOffers = formalOffersResponse!;

    const { previouslySelectedFormalOffer, previouslySelectedAmount } =
        getFormalOffersLatestOfferConfiguration({
            latestOfferConfiguration,
            formalOffers
        });

    useWriteOfferStep({
        offerId: previouslySelectedFormalOffer?.offer_id,
        step: 'offer',
        amount: previouslySelectedAmount ?? undefined
    });

    const [selectedOffer, setSelectedOffer] = useState<TFormalOffer | null>(
        previouslySelectedFormalOffer
    );
    const [selectedAmount, setSelectedAmount] = useState<number | null>(
        previouslySelectedAmount
    );

    const account = accountsResponse!.accounts.find(
        (account) => account.account_id === accountId
    )!;

    const loanOfferId = account.loan_offer_id!;
    const offerSetType = getOfferSetType(formalOffers, loanOfferId);
    const timeoutDate = getTimeOutDate(formalOffers);
    const outstandingBalance = account!.pending_balance!.total!;

    switch (offerSetType) {
        case 'more_funds_only':
            return (
                <MoreFundsOnly
                    formalOffers={formalOffers}
                    timeoutDate={timeoutDate}
                    offerSetType={offerSetType}
                    selectedAmount={selectedAmount!}
                    setSelectedAmount={setSelectedAmount}
                    outstandingBalance={outstandingBalance}
                    loanOfferId={loanOfferId}
                />
            );
        case 'more_funds_with_repayment_choice':
            return (
                <MoreFundsWithRepaymentChoice
                    timeoutDate={timeoutDate}
                    offerSetType={offerSetType}
                    formalOffers={formalOffers}
                    selectedOffer={selectedOffer}
                    setSelectedOffer={setSelectedOffer}
                    selectedAmount={selectedAmount}
                    setSelectedAmount={setSelectedAmount}
                    outstandingBalance={outstandingBalance}
                />
            );
        case 'repayment_reduction_only':
            return (
                <RepaymentReductionOnly
                    timeoutDate={timeoutDate}
                    offerSetType={offerSetType}
                    formalOffer={formalOffers[0]}
                    outstandingBalance={outstandingBalance}
                />
            );
        case 'repayment_reduction_with_choice':
            return (
                <RepaymentReductionWithChoice
                    timeoutDate={timeoutDate}
                    offerSetType={offerSetType}
                    formalOffers={formalOffers}
                    selectedOffer={selectedOffer}
                    setSelectedOffer={setSelectedOffer}
                    outstandingBalance={outstandingBalance}
                />
            );
        default:
            // TODO add logging for this case
            return <div>Error, unknown offer set</div>;
    }
};
